import React, { useState, useEffect } from 'react';
import { Users, Shield, UserPlus, Trash2, Edit, Save, X, Mail, Copy, CheckCircle, Clock, XCircle, Crown } from 'lucide-react';
import axios from 'axios';
import { API } from '../../lib/api';
import { useAuth } from '../../AuthContext';
import PlatformOwnersPanel from './PlatformOwnersPanel';

const UsersGroups = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Group creation state
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [availableTools, setAvailableTools] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Guest users state
  const [guestUsers, setGuestUsers] = useState([]);
  const [showGuestSection, setShowGuestSection] = useState(false);
  
  // Load data on mount
  useEffect(() => {
    loadData();
    loadPermissions();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, groupsRes, invitesRes, guestsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/groups`, { withCredentials: true }),
        axios.get(`${API}/admin/invites`, { withCredentials: true }),
        axios.get(`${API}/admin/users/guests`, { withCredentials: true }).catch(() => ({ data: { guests: [] } }))
      ]);
      // Handle both array and {users: [...]} response formats
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data.users || []);
      setUsers(usersData);
      setGroups(groupsRes.data);
      setInvites(invitesRes.data);
      setGuestUsers(guestsRes.data.guests || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const [toolsRes, sectionsRes] = await Promise.all([
        axios.get(`${API}/admin/permissions/tools`, { withCredentials: true }),
        axios.get(`${API}/admin/permissions/sections`, { withCredentials: true })
      ]);
      setAvailableTools(toolsRes.data.tools);
      setAvailableSections(sectionsRes.data.sections);
    } catch (err) {
      console.error('Failed to load permissions', err);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;
    
    try {
      await axios.delete(`${API}/admin/groups/${groupId}`, { withCredentials: true });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete group');
    }
  };

  const revokeInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to revoke this invite?')) return;
    
    try {
      await axios.delete(`${API}/admin/invites/${inviteId}`, { withCredentials: true });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke invite');
    }
  };

  const cleanupExpiredGuests = async () => {
    if (!window.confirm('Clean up all expired guest users?')) return;
    
    try {
      const response = await axios.post(`${API}/admin/users/cleanup-guests`, {}, { withCredentials: true });
      alert(`Cleaned up ${response.data.deleted_users} expired guest users`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cleanup guests');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-destructive/10 border border-destructive rounded-lg">
        <p className="text-destructive">{error}</p>
        <button 
          onClick={loadData}
          className="mt-2 text-sm text-primary hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Users & Groups Management</h2>
          <p className="text-muted-foreground">Manage users, groups, and permissions</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-muted p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-md transition-colors ${
            activeTab === 'users'
              ? 'bg-background shadow-sm'
              : 'hover:bg-background/50'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Users ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('groups')}
          className={`px-4 py-2 rounded-md transition-colors ${
            activeTab === 'groups'
              ? 'bg-background shadow-sm'
              : 'hover:bg-background/50'
          }`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          Groups ({groups.length})
        </button>
        <button
          onClick={() => setActiveTab('invites')}
          className={`px-4 py-2 rounded-md transition-colors ${
            activeTab === 'invites'
              ? 'bg-background shadow-sm'
              : 'hover:bg-background/50'
          }`}
        >
          <Mail className="w-4 h-4 inline mr-2" />
          Invites ({invites.filter(i => i.status === 'pending').length})
        </button>
        {isSuperAdmin && (
          <button
            onClick={() => setActiveTab('owners')}
            className={`px-4 py-2 rounded-md transition-colors ${
              activeTab === 'owners'
                ? 'bg-background shadow-sm'
                : 'hover:bg-background/50'
            }`}
            data-testid="tab-platform-owners"
          >
            <Crown className="w-4 h-4 inline mr-2 text-amber-400" />
            Owners
          </button>
        )}
      </div>

      {/* Content */}
      {activeTab === 'users' ? (
        <>
          <div className="flex justify-between items-center">
            {/* Guest Users Section */}
            {guestUsers.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowGuestSection(!showGuestSection)}
                  className="px-3 py-1.5 text-sm bg-yellow-500/10 text-yellow-600 rounded-lg hover:bg-yellow-500/20"
                >
                  <Clock className="w-4 h-4 inline mr-2" />
                  {guestUsers.length} Guest User{guestUsers.length !== 1 ? 's' : ''}
                  {showGuestSection ? ' ▼' : ' ▶'}
                </button>
                <button
                  onClick={cleanupExpiredGuests}
                  className="px-3 py-1.5 text-sm bg-destructive/10 text-destructive rounded-lg hover:bg-destructive/20"
                >
                  <Trash2 className="w-4 h-4 inline mr-2" />
                  Cleanup Expired
                </button>
              </div>
            )}
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Invite User
            </button>
          </div>

          {/* Guest Users Table (collapsible) */}
          {showGuestSection && guestUsers.length > 0 && (
            <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4 mb-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-600" />
                Guest Users (Auto-delete after 48 hours)
              </h3>
              <div className="space-y-2">
                {guestUsers.map((guest) => (
                  <div key={guest.user_id} className="flex items-center justify-between bg-background rounded-lg p-3 border">
                    <div className="flex-1">
                      <div className="font-medium">{guest.name}</div>
                      <div className="text-sm text-muted-foreground">{guest.email}</div>
                      <div className="text-xs text-yellow-600 mt-1">
                        {guest.is_expired ? (
                          <span className="text-destructive">❌ Expired</span>
                        ) : (
                          <span>⏱️ Expires in {Math.round(guest.hours_remaining)}h</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteUser(guest.user_id)}
                      className="px-3 py-1.5 text-sm bg-destructive/10 text-destructive rounded hover:bg-destructive/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <UsersTable users={users} groups={groups} onDelete={deleteUser} onUpdate={loadData} />
        </>
      ) : activeTab === 'groups' ? (
        <GroupsManager
          groups={groups}
          availableTools={availableTools}
          availableSections={availableSections}
          onDelete={deleteGroup}
          onUpdate={loadData}
        />
      ) : activeTab === 'invites' ? (
        <>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              <Mail className="w-4 h-4 inline mr-2" />
              Invite User(s)
            </button>
          </div>
          <InvitesTable invites={invites} groups={groups} onRevoke={revokeInvite} />
        </>
      ) : activeTab === 'owners' && isSuperAdmin ? (
        <PlatformOwnersPanel />
      ) : (
        <div className="text-sm text-muted-foreground">Unknown tab.</div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteUserModal
          groups={groups}
          onClose={() => setShowInviteModal(false)}
          onSuccess={loadData}
        />
      )}
    </div>
  );
};

// Users Table Component
const UsersTable = ({ users, groups, onDelete, onUpdate }) => {
  const [editingUser, setEditingUser] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const startEdit = (user) => {
    setEditingUser(user.user_id);
    setSelectedGroups(user.groups || []);
  };

  const saveUserGroups = async (userId) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/groups`,
        { group_ids: selectedGroups },
        { withCredentials: true }
      );
      setEditingUser(null);
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update user groups');
    }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/role`,
        newRole,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to change role');
    }
  };

  return (
    <div className="bg-card rounded-lg border overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted">
          <tr>
            <th className="text-left p-4">User</th>
            <th className="text-left p-4">Role</th>
            <th className="text-left p-4">Groups</th>
            <th className="text-left p-4">Created</th>
            <th className="text-right p-4">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {users.map((user) => (
            <tr key={user.user_id} className="hover:bg-muted/50">
              <td className="p-4">
                <div>
                  <div className="font-medium">{user.name}</div>
                  <div className="text-sm text-muted-foreground">{user.email}</div>
                  {user.is_guest && (
                    <span className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-0.5 rounded">
                      Guest
                    </span>
                  )}
                </div>
              </td>
              <td className="p-4">
                <select
                  value={user.role}
                  onChange={(e) => changeRole(user.user_id, e.target.value)}
                  className="px-3 py-1 rounded border bg-background"
                  data-testid={`role-select-${user.user_id}`}
                >
                  <option value="super_admin">Super Admin</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
              </td>
              <td className="p-4">
                {editingUser === user.user_id ? (
                  <div className="space-y-2">
                    {groups.map((group) => (
                      <label key={group.group_id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedGroups.includes(group.group_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedGroups([...selectedGroups, group.group_id]);
                            } else {
                              setSelectedGroups(selectedGroups.filter(id => id !== group.group_id));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{group.name}</span>
                      </label>
                    ))}
                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={() => saveUserGroups(user.user_id)}
                        className="px-3 py-1 bg-primary text-primary-foreground rounded text-sm"
                      >
                        <Save className="w-3 h-3 inline mr-1" />
                        Save
                      </button>
                      <button
                        onClick={() => setEditingUser(null)}
                        className="px-3 py-1 bg-muted rounded text-sm"
                      >
                        <X className="w-3 h-3 inline mr-1" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {(user.groups || []).length === 0 ? (
                      <span className="text-sm text-muted-foreground">No groups</span>
                    ) : (
                      user.groups.map((groupId) => {
                        const group = groups.find(g => g.group_id === groupId);
                        return group ? (
                          <span
                            key={groupId}
                            className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs"
                          >
                            {group.name}
                          </span>
                        ) : null;
                      })
                    )}
                  </div>
                )}
              </td>
              <td className="p-4 text-sm text-muted-foreground">
                {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </td>
              <td className="p-4 text-right">
                <div className="flex justify-end space-x-2">
                  {editingUser !== user.user_id && (
                    <button
                      onClick={() => startEdit(user)}
                      className="p-2 hover:bg-muted rounded"
                      title="Edit groups"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  {!user.is_guest && (
                    <button
                      onClick={() => onDelete(user.user_id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded"
                      title="Delete user"
                      data-testid={`delete-user-${user.user_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {user.is_guest && (
                    <button
                      onClick={() => onDelete(user.user_id)}
                      className="p-2 hover:bg-destructive/10 text-destructive rounded"
                      title="Delete guest user"
                      data-testid={`delete-guest-${user.user_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Groups Manager Component
const GroupsManager = ({ groups, availableTools, availableSections, onDelete, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => {
            setEditingGroup(null);
            setShowForm(true);
          }}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
        >
          <UserPlus className="w-4 h-4 inline mr-2" />
          Create Group
        </button>
      </div>

      {showForm && (
        <GroupForm
          group={editingGroup}
          availableTools={availableTools}
          availableSections={availableSections}
          onClose={() => {
            setShowForm(false);
            setEditingGroup(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingGroup(null);
            onUpdate();
          }}
        />
      )}

      <div className="grid gap-4">
        {groups.map((group) => (
          <div key={group.group_id} className="bg-card rounded-lg border p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold">{group.name}</h3>
                {group.description && (
                  <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
                )}
                <div className="mt-4 text-sm">
                  <div className="text-muted-foreground mb-2">Permissions:</div>
                  <div className="space-y-1">
                    <div>
                      <span className="font-medium">Tools: </span>
                      {Object.keys(group.permissions?.tools || {}).length || 0} configured
                    </div>
                    <div>
                      <span className="font-medium">Sections: </span>
                      {Object.keys(group.permissions?.sections || {}).length || 0} configured
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingGroup(group);
                    setShowForm(true);
                  }}
                  className="p-2 hover:bg-muted rounded"
                  title="Edit group"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(group.group_id)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded"
                  title="Delete group"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {groups.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            No groups created yet. Create your first group to manage permissions.
          </div>
        )}
      </div>
    </div>
  );
};

// Group Form Component (will create separately for better organization)
const GroupForm = ({ group, availableTools, availableSections, onClose, onSave }) => {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [toolPermissions, setToolPermissions] = useState(group?.permissions?.tools || {});
  const [sectionPermissions, setSectionPermissions] = useState(group?.permissions?.sections || {});
  const [saving, setSaving] = useState(false);

  const togglePermission = (type, id, perm) => {
    const current = type === 'tool' ? toolPermissions : sectionPermissions;
    const setter = type === 'tool' ? setToolPermissions : setSectionPermissions;
    
    setter({
      ...current,
      [id]: {
        ...(current[id] || { read: false, write: false, execute: false }),
        [perm]: !current[id]?.[perm]
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        name,
        description,
        permissions: {
          tools: toolPermissions,
          sections: sectionPermissions
        }
      };

      if (group) {
        await axios.put(`${API}/admin/groups/${group.group_id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/groups`, data, { withCredentials: true });
      }
      
      onSave();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save group');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold">
              {group ? 'Edit Group' : 'Create New Group'}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-muted rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Group Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-3 py-2 border rounded-lg bg-background"
                placeholder="e.g., DevOps Team"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-background"
                rows="2"
                placeholder="Brief description of this group"
              />
            </div>
          </div>

          {/* Tools Permissions */}
          <div>
            <h4 className="font-semibold mb-3">Tools Permissions</h4>
            <div className="space-y-2">
              {availableTools.map((tool) => (
                <PermissionRow
                  key={tool.id}
                  id={tool.id}
                  name={tool.name}
                  description={tool.description}
                  permissions={toolPermissions[tool.id] || { read: false, write: false, execute: false }}
                  onChange={(perm) => togglePermission('tool', tool.id, perm)}
                />
              ))}
            </div>
          </div>

          {/* Sections Permissions */}
          <div>
            <h4 className="font-semibold mb-3">Sections Permissions</h4>
            <div className="space-y-2">
              {availableSections.map((section) => (
                <PermissionRow
                  key={section.id}
                  id={section.id}
                  name={section.name}
                  description={section.description}
                  permissions={sectionPermissions[section.id] || { read: false, write: false, execute: false }}
                  onChange={(perm) => togglePermission('section', section.id, perm)}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : group ? 'Update Group' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Permission Row Component
const PermissionRow = ({ id, name, description, permissions, onChange }) => {
  return (
    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
      <div className="flex-1">
        <div className="font-medium">{name}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <div className="flex space-x-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={permissions.read}
            onChange={() => onChange('read')}
            className="rounded"
          />
          <span className="text-sm">Read</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={permissions.write}
            onChange={() => onChange('write')}
            className="rounded"
          />
          <span className="text-sm">Write</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={permissions.execute}
            onChange={() => onChange('execute')}
            className="rounded"
          />
          <span className="text-sm">Execute</span>
        </label>
      </div>
    </div>
  );
};

// Invites Table Component
const InvitesTable = ({ invites, groups, onRevoke }) => {
  const [copiedToken, setCopiedToken] = useState(null);

  const copyInviteLink = (invite) => {
    const link = `${window.location.origin}/accept-invite?token=${invite.token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(invite.invite_id);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expired':
      case 'revoked':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'accepted':
        return 'bg-green-500/10 text-green-500';
      case 'expired':
      case 'revoked':
        return 'bg-red-500/10 text-red-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  return (
    <div className="bg-card rounded-lg border overflow-hidden">
      <table className="w-full">
        <thead className="bg-muted">
          <tr>
            <th className="text-left p-4">Email</th>
            <th className="text-left p-4">Group</th>
            <th className="text-left p-4">Status</th>
            <th className="text-left p-4">Expires</th>
            <th className="text-right p-4">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {invites.map((invite) => {
            const group = groups.find(g => g.group_id === invite.group_id);
            const expiresAt = new Date(invite.expires_at);
            const isExpired = expiresAt < new Date();
            
            return (
              <tr key={invite.invite_id} className="hover:bg-muted/50">
                <td className="p-4">
                  <div className="font-medium">{invite.email}</div>
                </td>
                <td className="p-4">
                  <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                    {group?.name || 'Unknown'}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(invite.status)}
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(invite.status)}`}>
                      {invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                    </span>
                  </div>
                </td>
                <td className="p-4 text-sm text-muted-foreground">
                  {expiresAt.toLocaleDateString()} {expiresAt.toLocaleTimeString()}
                  {isExpired && invite.status === 'pending' && (
                    <span className="ml-2 text-red-500">(Expired)</span>
                  )}
                </td>
                <td className="p-4 text-right">
                  <div className="flex justify-end space-x-2">
                    {invite.status === 'pending' && (
                      <>
                        <button
                          onClick={() => copyInviteLink(invite)}
                          className="p-2 hover:bg-muted rounded"
                          title="Copy invite link"
                        >
                          {copiedToken === invite.invite_id ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => onRevoke(invite.invite_id)}
                          className="p-2 hover:bg-destructive/10 text-destructive rounded"
                          title="Revoke invite"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {invites.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          No invites found
        </div>
      )}
    </div>
  );
};

// Invite User Modal Component
const InviteUserModal = ({ groups, onClose, onSuccess }) => {
  const [emails, setEmails] = useState('');
  const [groupId, setGroupId] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [inviteLinks, setInviteLinks] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSending(true);

    try {
      // Parse emails - support comma-separated, newline-separated, or space-separated
      const emailList = emails
        .split(/[\n,\s]+/)
        .map(e => e.trim())
        .filter(e => e.length > 0);

      if (emailList.length === 0) {
        setError('Please enter at least one email address');
        setSending(false);
        return;
      }

      // Create invites for all emails
      const results = [];
      for (const email of emailList) {
        try {
          const response = await axios.post(
            `${API}/admin/invites`,
            { email, group_id: groupId },
            { withCredentials: true }
          );

          const link = `${window.location.origin}/accept-invite?token=${response.data.token}`;
          const delivery = response.data.email_delivery || {};
          results.push({
            email,
            link,
            success: true,
            email_sent: !!delivery.sent,
            email_skipped: !!delivery.skipped,
            email_error: delivery.error || null,
          });
        } catch (err) {
          results.push({ email, error: err.response?.data?.detail || 'Failed', success: false });
        }
      }
      
      setInviteLinks(results);
      
      setTimeout(() => {
        onSuccess();
        if (results.every(r => r.success)) {
          onClose();
        }
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create invite');
      setSending(false);
    }
  };

  const copyAllLinks = () => {
    const links = inviteLinks.filter(r => r.success).map(r => `${r.email}: ${r.link}`).join('\n');
    navigator.clipboard.writeText(links);
    alert('All invite links copied to clipboard!');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">Invite User(s)</h3>
        
        {inviteLinks.length > 0 ? (
          <div className="space-y-4">
            <div className="p-4 bg-green-500/10 border border-green-500 rounded-lg">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-500">
                    {inviteLinks.filter(r => r.success).length} Invite(s) Created!
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Copy the links below and share them with users:
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {inviteLinks.map((result, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <label className="text-sm font-medium text-foreground">{result.email}</label>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {result.success ? (
                        <span className="text-xs text-green-500">✓ Created</span>
                      ) : (
                        <span className="text-xs text-destructive">✗ {result.error}</span>
                      )}
                      {result.success && result.email_sent && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-500/10 text-green-500 border border-green-500/30" data-testid={`invite-email-sent-${index}`}>email sent</span>
                      )}
                      {result.success && result.email_skipped && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/30" title="Add RESEND_API_KEY to backend .env to enable email delivery" data-testid={`invite-email-skipped-${index}`}>email not configured</span>
                      )}
                      {result.success && !result.email_sent && !result.email_skipped && result.email_error && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/10 text-red-500 border border-red-500/30" title={result.email_error} data-testid={`invite-email-failed-${index}`}>email failed</span>
                      )}
                    </div>
                  </div>
                  {result.success && (
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={result.link}
                        readOnly
                        className="flex-1 px-3 py-2 border rounded-lg bg-muted text-foreground text-sm"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(result.link);
                          alert('Link copied!');
                        }}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {inviteLinks.some(r => r.success) && (
              <button
                onClick={copyAllLinks}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Copy All Links
              </button>
            )}
            
            <p className="text-xs text-muted-foreground">
              Links expire in 7 days. Users will be automatically added to the selected group after signing in with Google.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive rounded-lg text-destructive text-sm">
                {error}
              </div>
            )}
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-foreground">Email Address(es)</label>
                <button
                  type="button"
                  onClick={() => setBulkMode(!bulkMode)}
                  className="text-xs text-primary hover:underline"
                >
                  {bulkMode ? 'Single Email' : 'Bulk Invite'}
                </button>
              </div>
              {bulkMode ? (
                <textarea
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  required
                  rows={6}
                  className="w-full px-3 py-2 border rounded-lg bg-background text-foreground placeholder:text-muted-foreground"
                  placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com&#10;&#10;Separate emails by newline, comma, or space"
                />
              ) : (
                <input
                  type="email"
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg bg-background text-foreground placeholder:text-muted-foreground"
                  placeholder="user@example.com"
                />
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {bulkMode ? 'Enter multiple emails (one per line, or comma/space separated)' : 'Click "Bulk Invite" above to invite multiple users at once'}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Assign to Group</label>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                required
                className="w-full px-3 py-2 border rounded-lg bg-background text-foreground"
              >
                <option value="" className="text-muted-foreground">Select a group...</option>
                {groups.map((group) => (
                  <option key={group.group_id} value={group.group_id} className="text-foreground">
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="px-4 py-2 border rounded-lg hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {sending ? 'Creating...' : 'Send Invite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UsersGroups;
